import React, { useState, useEffect, act } from "react";
import axios from "axios";
import { ZM_REGION_WISE_DETAILS } from "../../constants";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  ChevronUp,
  ChevronDown,
  Info,
  Calendar,
  Clock,
  MapPin,
  FileText,
  MessageSquare,
  Image,
  Users,
  Phone,
  Map,
  ArrowRight,
} from "lucide-react";
import "chart.js/auto";
import dayjs from "dayjs";
import FilterListIcon from "@mui/icons-material/FilterList";
import {
  Button,
  Grid,
  IconButton,
  TextField,
  Box,
  useMediaQuery,
  Typography,
  Dialog,
  DialogContent,
} from "@mui/material";
import { useTheme } from "@emotion/react";
import { Doughnut } from "react-chartjs-2";
import { Commet } from "react-loading-indicators";
import CloseIcon from "@mui/icons-material/Close";

const COLORS = [
  "#0088FE",
  "#00C49F",
  "#FFBB28",
  "#FF8042",
  "#8884d8",
  "#82ca9d",
];

const BAR_COLORS = [
  "#4f46e5",
  "#f87171",
  "#34d399",
  "#fbbf24",
  "#3b82f6",
  "#9333ea",
  "#ef4444",
]; // Define color palette

const buttonStyle = {
  border: "1px solid #fff",
  borderRadius: "4px",
  margin: "5px",
  width: "40px",
  height: "40px",
  justifyContent: "center",
  alignItems: "center",
  backgroundColor: "#097969",
  color: "#fff",
  transition: "background-color 0.3s, transform 0.2s",
  boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.2)",
};

const hoverEffects = (e, isHovering) => {
  e.currentTarget.style.backgroundColor = isHovering ? "#0B9B73" : "#097969";
  e.currentTarget.style.transform = isHovering ? "scale(1.05)" : "scale(1)";
};

const MobileView = ({}) => {
  const [selectedRegion, setSelectedRegion] = useState(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isMobileOrPhablet = useMediaQuery("(max-width: 1080px)");

  const [filterShow, setFilterShow] = useState(false);
  const [startDate, setStartDate] = useState(
    dayjs().subtract(7, "day").format("YYYY-MM-DD") // Last 7 days
  );
  const [endDate, setEndDate] = useState(
    dayjs().subtract(1, "day").format("YYYY-MM-DD") // Yesterday
  );

  const [loading, setLoading] = useState(false);
  const [regionWiseData, setRegionWiseData] = useState(null);

  const [dateRange, setDateRange] = useState({
    startDate: dayjs().subtract(7, "day").format("YYYY-MM-DD"), // Last 7 days
    endDate: dayjs().format("YYYY-MM-DD"), // Today
  });

  const fetchRegionWiseData = async (selectedStartDate, selectedEndDate) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${ZM_REGION_WISE_DETAILS}?employeeId=${localStorage.getItem(
          "username"
        )}&startDate=${selectedStartDate}&endDate=${selectedEndDate}`
      );

      if (
        response.data?.dateRange?.startDate &&
        response.data?.dateRange?.endDate
      ) {
        setDateRange({
          startDate: dayjs(response.data.startDate).format("YYYY-MM-DD"),
          endDate: dayjs(response.data.endDate).format("YYYY-MM-DD"),
        });
      }

      setRegionWiseData(response.data);
    } catch (err) {
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch data initially with last 7 days range
  useEffect(() => {
    fetchRegionWiseData(startDate, endDate);
  }, []);

  // Apply filter
  const handleApply = () => {
    fetchRegionWiseData(startDate, endDate); // Fetch data for selected dates
    setFilterShow(false);
    localStorage.setItem("startDate", startDate);
    localStorage.setItem("endDate", endDate);
  };

  // Clear filter - Reset to last 7 days
  const handleClear = () => {
    const defaultStartDate = dayjs().subtract(7, "day").format("YYYY-MM-DD");
    const defaultEndDate = dayjs().subtract(1, "day").format("YYYY-MM-DD");

    setStartDate(defaultStartDate);
    setEndDate(defaultEndDate);
    fetchRegionWiseData(defaultStartDate, defaultEndDate);

    localStorage.removeItem("startDate");
    localStorage.removeItem("endDate");
    setFilterShow(false);
  };

  const [expandedRegion, setExpandedRegion] = useState(null);

  // Process data for region action counts
  const regionActionData = regionWiseData?.regions?.map((region) => {
    const totalActions = region.bdms.reduce(
      (sum, bdm) => sum + bdm.actionCount,
      0
    );
    return {
      name: region.RegionName,
      actions: totalActions,
    };
  });

  // Process data for task types
  const getAllActions = () => {
    let allActions = [];
    regionWiseData?.regions?.forEach((region) => {
      region.bdms.forEach((bdm) => {
        allActions = [...allActions, ...bdm.actions];
      });
    });
    return allActions;
  };

  const taskTypeData = (() => {
    const actions = getAllActions();
    const taskTypes = {};

    actions.forEach((action) => {
      if (!taskTypes[action.task_type]) {
        taskTypes[action.task_type] = 0;
      }
      taskTypes[action.task_type]++;
    });

    return Object.keys(taskTypes).map((type) => ({
      name:
        type === "HO_task"
          ? "HO Task"
          : type === "self_task"
          ? "Self Task"
          : "Other Task",
      value: taskTypes[type],
    }));
  })();

  // Process data for completion status
  const completionStatusData = (() => {
    const actions = getAllActions();
    const completed = actions.filter(
      (a) => a.completion_status === "completed"
    ).length;
    const notCompleted = actions.filter(
      (a) => a.completion_status === "not_completed"
    ).length;

    return [
      { name: "Completed", value: completed },
      { name: "Not Completed", value: notCompleted },
    ];
  })();

  const toggleRegion = (regionId) => {
    if (expandedRegion === regionId) {
      setExpandedRegion(null);
    } else {
      setExpandedRegion(regionId);
    }
  };

  // Prepare Chart.js data
  const data = {
    labels: taskTypeData.map((item) => item.name),
    datasets: [
      {
        data: taskTypeData.map((item) => item.value),
        backgroundColor: COLORS.slice(0, taskTypeData.length), // Assign colors
        hoverOffset: 4, // Adds hover effect spacing
      },
    ],
  };

  // Dynamic font size based on screen width
  const options = {
    plugins: {
      legend: {
        display: true,
        position: "bottom",
        labels: {
          font: {
            size: window.innerWidth < 640 ? 10 : 14, // Adjust font size for mobile
          },
        },
      },
      tooltip: {
        callbacks: {
          label: (tooltipItem) => {
            const dataset = tooltipItem.dataset;
            const total = dataset.data.reduce((acc, val) => acc + val, 0);
            const value = dataset.data[tooltipItem.dataIndex];
            const percentage = ((value / total) * 100).toFixed(0);
            return `${dataset.labels[tooltipItem.dataIndex]}: ${percentage}%`;
          },
        },
      },
      datalabels: {
        color: "white", // Change text color to white
        font: {
          weight: "bold", // Make text bold
          size: 14, // Set a readable font size
        },
        formatter: (value, context) => value,
      },
    },
    maintainAspectRatio: false, // Ensures responsive behavior
  };

  // Prepare Chart.js data
  const Completiondata = {
    labels: completionStatusData.map((item) => item.name),
    datasets: [
      {
        data: completionStatusData.map((item) => item.value),
        backgroundColor: ["#4ade80", "#f87171"], // Colors matching Recharts
        hoverOffset: 4, // Adds hover effect spacing
      },
    ],
  };

  // Chart.js options
  const CompletionOption = {
    plugins: {
      legend: {
        display: true,
        position: "bottom",
        labels: {
          font: {
            size: window.innerWidth < 640 ? 10 : 14, // Adjust font size for mobile
            fontWeight: 700,
          },
        },
      },
      tooltip: {
        callbacks: {
          label: (tooltipItem) => {
            const dataset = tooltipItem.dataset;
            const total = dataset.data.reduce((acc, val) => acc + val, 0);
            const value = dataset.data[tooltipItem.dataIndex];
            const percentage = ((value / total) * 100).toFixed(0);
            return `${dataset.labels[tooltipItem.dataIndex]}: ${percentage}%`;
          },
        },
      },
      datalabels: {
        color: "white", // Change text color to white
        font: {
          weight: "bold", // Make text bold
          size: 14, // Set a readable font size
        },
        formatter: (value, context) => value,
      },
    },
    cutout: "60%", // Converts Pie Chart to Doughnut
    maintainAspectRatio: false, // Ensures responsiveness
  };

  // if (loading) {
  //     return (
  //       <div className="flex justify-center items-center min-h-screen">
  //         <Commet color="#32cd32" size="medium" text="Loading" textColor="" />
  //       </div>
  //     );
  //   }

  const [expandedRegion1, setExpandedRegion1] = useState(null);
  const [expandedAction, setExpandedAction] = useState(null);

  const toggleRegion1 = (regionId) => {
    setExpandedRegion(expandedRegion === regionId ? null : regionId);
    setExpandedAction(null); // Reset expanded action when toggling regions
  };

  const toggleActionDetails = (actionId) => {
    setExpandedAction(expandedAction === actionId ? null : actionId);
  };

  // Format date to readable format
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // State to handle dialog
  const [openImageDialog, setOpenImageDialog] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  // Handle image preview
  const handleImageClick = (imgUrl) => {
    setSelectedImage(imgUrl);
    setOpenImageDialog(true);
  };

  return (
    <>
      <>
        <div className="flex flex-col bg-gray-100 min-h-screen">
          {/* Header with Filter */}
          <div className="bg-indigo-500 text-white p-3 rounded-lg w-[100%] max-w-lg mx-auto shadow-md">
            {/* Title and Employee Info */}
            <div>
              <h1 className="text-lg font-semibold">
                Dashboard - Zonal Manager
              </h1>
              <p className="text-sm opacity-90">
                {regionWiseData?.employeeInfo?.EmployeeName || "N/A"} -{" "}
                {regionWiseData?.employeeInfo?.EmployeeId || "N/A"}
              </p>
            </div>
          </div>

          {/* Date Selector */}
          <div className="bg-white p-4 m-2 rounded-lg shadow flex items-center justify-between">
            <span className="font-medium text-gray-700">Duration</span>
            <div className="flex items-center text-sm text-blue-600">
              <Calendar size={16} className="mr-1" />
              <span>
                {dayjs(regionWiseData?.dateRange?.startDate).format(
                  "DD-MM-YYYY"
                ) || "N/A"}{" "}
                to{" "}
                {dayjs(regionWiseData?.dateRange?.endDate).format(
                  "DD-MM-YYYY"
                ) || "N/A"}
              </span>
            </div>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                position: "relative",
              }}
            >
              {/* Filter Icon */}
              <IconButton
                onClick={() => setFilterShow((prev) => !prev)}
                style={buttonStyle}
                onMouseEnter={(e) => hoverEffects(e, true)}
                onMouseLeave={(e) => hoverEffects(e, false)}
              >
                <FilterListIcon />
              </IconButton>

              {/* Filter UI (Dropdown) */}
              {filterShow && (
                <Box
                  sx={{
                    position: "absolute",
                    top: "110%",
                    right: 0,
                    backgroundColor: "#f0f4f8",
                    padding: 1.5,
                    borderRadius: 2,
                    boxShadow: 3,
                    width: isMobileOrPhablet ? "100%" : "350px",
                    maxWidth: "350px",
                    minWidth: "250px",
                    zIndex: 10,
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                  }}
                >
                  <Grid container spacing={2}>
                    {/* Start Date Picker */}
                    <Grid item xs={12}>
                      <TextField
                        label="Start Date"
                        type="date"
                        fullWidth
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                      />
                    </Grid>

                    {/* End Date Picker */}
                    <Grid item xs={12}>
                      <TextField
                        label="End Date"
                        type="date"
                        fullWidth
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                      />
                    </Grid>

                    {/* Action Buttons */}
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          justifyContent: "flex-end",
                        }}
                      >
                        <Button
                          onClick={handleClear}
                          variant="outlined"
                          color="secondary"
                        >
                          Clear
                        </Button>
                        <Button
                          onClick={handleApply}
                          variant="contained"
                          color="primary"
                        >
                          Apply
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Box>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-2 gap-2 p-2">
            <div className="bg-white p-3 rounded-lg shadow flex flex-col text-center">
              <span className="text-xs text-gray-500">Total Regions</span>
              <span className="text-2xl font-bold">
                {regionWiseData?.overallSummary?.totalRegions ?? 0}
              </span>
            </div>
            <div className="bg-white p-3 rounded-lg shadow flex flex-col text-center">
              <span className="text-xs text-gray-500">Total Actions</span>
              <span className="text-2xl font-bold">
                {getAllActions()?.length ?? 0}
              </span>
            </div>
          </div>

          {/* Region Actions Bar Chart */}
          <div className="bg-white m-2 p-4 rounded-lg shadow">
            <h2 className="text-lg font-semibold mb-3">Actions by Region</h2>
            <div className="h-64">
              {regionActionData?.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={regionActionData}
                    margin={{ top: 10, right: 10, left: 0, bottom: 40 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="name"
                      angle={-90}
                      textAnchor="end"
                      height={60}
                    />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="actions" name="Actions">
                      {regionActionData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={BAR_COLORS[index % BAR_COLORS.length]}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-gray-500 text-sm text-center">
                  No data available
                </p>
              )}
            </div>
          </div>

          {/* Task Type & Completion Status Pie Charts */}
          <div className="grid grid-cols-2 gap-2 p-2">
            <div className="bg-white p-3 rounded-lg shadow">
              <h2 className="text-sm font-semibold mb-2">Task Types</h2>
              <div className="h-40">
                {taskTypeData?.length > 0 ? (
                  <Doughnut data={data} options={options} />
                ) : (
                  <p className="text-gray-500 text-sm text-center">
                    No data available
                  </p>
                )}
              </div>
            </div>

            <div className="bg-white p-3 rounded-lg shadow">
              <h2 className="text-sm font-semibold mb-2">Completion Status</h2>
              <div className="h-40">
                {completionStatusData?.length > 0 ? (
                  <Doughnut data={Completiondata} options={CompletionOption} />
                ) : (
                  <p className="text-gray-500 text-sm text-center">
                    No data available
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Region Details */}
          <div className="bg-white m-2 p-4 rounded-lg shadow">
            <h2 className="text-lg font-semibold mb-3">Region Details</h2>

            {regionWiseData?.regions?.length > 0 ? (
              regionWiseData.regions.map((region) => (
                <div
                  key={region?.RegionId}
                  className="mb-3 border rounded-lg overflow-hidden shadow-sm"
                >
                  <div
                    className="flex justify-between items-center p-3 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => toggleRegion(region?.RegionId)}
                  >
                    <div>
                      <h3 className="font-medium">
                        {region?.RegionName || "N/A"}
                      </h3>
                      <p className="text-xs text-gray-500">
                        {region?.Project || "N/A"} • {region?.bdms?.length || 0}{" "}
                        BDMs
                      </p>
                    </div>
                    <div className="flex items-center">
                      <span className="mr-2 bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                        {region?.bdms?.reduce(
                          (sum, bdm) => sum + (bdm?.actionCount || 0),
                          0
                        )}{" "}
                        Actions
                      </span>
                      {expandedRegion === region?.RegionId ? (
                        <ChevronUp size={18} />
                      ) : (
                        <ChevronDown size={18} />
                      )}
                    </div>
                  </div>

                  {expandedRegion === region?.RegionId && (
                    <div className="p-3">
                      {region?.bdms?.map((bdm) => (
                        <div
                          key={bdm?.EmployeeId}
                          className="mb-4 last:mb-0 p-3 bg-gray-50 rounded-lg"
                        >
                          <div className="flex justify-between items-center mb-2">
                            <div>
                              <p className="font-medium text-base">
                                {bdm?.EmployeeName || "N/A"}
                              </p>
                              <p className="text-xs text-gray-500">
                                {bdm?.is_zonal_manager
                                  ? "Zonal Manager"
                                  : bdm?.is_bdm
                                  ? "BDM"
                                  : "Employee"}{" "}
                                • {bdm?.actionCount || 0} Actions
                              </p>
                            </div>

                            {/* {bdm?.activitySummary && (
                        <div className="flex flex-wrap gap-1">
                          {bdm.activitySummary.roVisits > 0 && (
                            <span className="text-xs bg-purple-100 text-purple-800 px-2 py-0.5 rounded-full">
                              {bdm.activitySummary.roVisits} RO Visits
                            </span>
                          )}
                          {bdm.activitySummary.hoVisits > 0 && (
                            <span className="text-xs bg-indigo-100 text-indigo-800 px-2 py-0.5 rounded-full">
                              {bdm.activitySummary.hoVisits} HO Visits
                            </span>
                          )}
                          {bdm.activitySummary.boVisits > 0 && (
                            <span className="text-xs bg-teal-100 text-teal-800 px-2 py-0.5 rounded-full">
                              {bdm.activitySummary.boVisits} BO Visits
                            </span>
                          )}
                        </div>
                      )} */}
                          </div>

                          {bdm?.actions?.length > 0 ? (
                            <div className="pl-3 border-l-2 border-gray-200 mt-3">
                              {bdm.actions.map((action) => (
                                <div
                                  key={action?.id}
                                  className="mb-3 last:mb-0 bg-white p-3 rounded-lg border shadow-sm"
                                >
                                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
                                    {/* Left: Task Info */}
                                    <div className="flex-1">
                                      <span className="font-medium text-sm block">
                                        {action?.task_name ||
                                          action?.specific_action ||
                                          action?.task_type ||
                                          "N/A"}
                                      </span>
                                      <p className="text-xs text-gray-500 mt-1 flex items-center">
                                        <Clock size={12} className="mr-1" />
                                        {formatDate(action?.action_date)}
                                      </p>
                                    </div>

                                    {/* Right: Status + Chevron */}
                                    <div className="flex flex-row sm:flex-col justify-between items-end sm:items-end sm:h-full gap-1">
                                      {/* Top right: Status */}
                                      <span
                                        className={`text-xs px-2 py-0.5 rounded-full ${
                                          action?.completion_status ===
                                          "completed"
                                            ? "bg-green-100 text-green-800"
                                            : "bg-red-100 text-red-800"
                                        }`}
                                      >
                                        {action?.completion_status ===
                                        "completed"
                                          ? "Completed"
                                          : "Not Completed"}
                                      </span>

                                      {/* Bottom right: Chevron */}
                                      <button
                                        className="text-gray-500 hover:text-blue-600"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          toggleActionDetails(action?.id);
                                        }}
                                      >
                                        {expandedAction === action?.id ? (
                                          <ChevronUp size={16} />
                                        ) : (
                                          <ChevronDown size={16} />
                                        )}
                                      </button>
                                    </div>
                                  </div>

                                  {/* <div className="flex flex-wrap gap-1 mt-2">
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                action?.task_type === "HO_task" 
                                  ? "bg-blue-100 text-blue-800"
                                  : action?.task_type === "self_task"
                                  ? "bg-amber-100 text-amber-800"
                                  : "bg-gray-100 text-gray-800"
                              }`}>
                                {action?.task_type === "HO_task"
                                  ? "HO Task"
                                  : action?.task_type === "self_task"
                                  ? "Self Task"
                                  : action?.task_type || "Task"}
                              </span>
                              
                              {action?.action_type && (
                                <span className="text-xs bg-gray-100 text-gray-800 px-2 py-0.5 rounded-full">
                                  {action?.action_type}
                                </span>
                              )}
                            </div> */}

{expandedAction === action?.id && (
  <div className="mt-4 pt-3 border-t border-gray-200 rounded-lg overflow-hidden transition-all duration-300 ease-in-out">
    {/* Travel Information Section */}
    {action.task_name === "Travel" && (
      <div className="mb-4 animate-fadeIn">
        <div className="flex items-center text-sm font-medium text-gray-800 mb-2">
          <MapPin size={16} className="mr-2 text-blue-500" />
          <span>Travel Information</span>
        </div>
        <div className="bg-gray-50 p-3 rounded-lg shadow-sm hover:shadow-md transition-shadow">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            
              <div className="flex flex-col">
                <span className="text-xs text-gray-500">From</span>
                <span className="text-sm font-medium">{action?.travel_form_details?.travelFrom || "-"}</span>
              </div>
            
            
              <div className="flex flex-col">
                <span className="text-xs text-gray-500">To</span>
                <span className="text-sm font-medium">{action?.travel_form_details?.travelTo || "-"}</span>
              </div>
            
            
              <div className="flex flex-col">
                <span className="text-xs text-gray-500">Mode of Travel</span>
                <span className="text-sm font-medium text-capitalize">{action?.travel_form_details?.modeOfTravel || "-"}</span>
              </div>
            
            
              <div className="flex flex-col">
                <span className="text-xs text-gray-500">Purpose</span>
                <span className="text-sm font-medium">{action?.travel_form_details?.reasonForTravel || "-"}</span>
              </div>
            
          </div>
          
            <div className="mt-3 pt-3 border-t border-gray-200">
              <span className="text-xs text-gray-500">Remarks</span>
              <p className="text-sm mt-1">{action?.travel_form_details?.remarks || "-"}</p>
            </div>
          
          <div className="mt-3 space-y-3">
                                                        <div className="flex justify-between items-center">
                                                          <span className="text-sm font-medium">
                                                            Visit Image:
                                                          </span>
                                                          {action?.travel_form_details
                                                            ?.mandatoryVisitImage ? (
                                                            <button
                                                              className="flex items-center justify-center bg-gray-100 hover:bg-gray-200 text-blue-500 px-3 py-1 rounded transition-colors"
                                                              onClick={() => {
                                                                setSelectedImage(
                                                                  action
                                                                    .travel_form_details
                                                                    .mandatoryVisitImage
                                                                );
                                                                setOpenImageDialog(true);
                                                              }}
                                                            >
                                                              <span className="ml-1 text-xs">
                                                                View
                                                              </span>
                                                              <ArrowRight className="h-3 w-3 ml-1" />
                                                            </button>
                                                          ) : (
                                                            <span className="text-sm text-gray-500">
                                                              No image
                                                            </span>
                                                          )}
                                                        </div>
          
                                                        <div className="flex justify-between items-center">
                                                          <span className="text-sm font-medium">
                                                            Optional Visit Image:
                                                          </span>
                                                          {action?.travel_form_details
                                                            ?.optionalVisitImage ? (
                                                            <button
                                                              className="flex items-center justify-center bg-gray-100 hover:bg-gray-200 text-blue-500 px-3 py-1 rounded transition-colors"
                                                              onClick={() => {
                                                                setSelectedImage(
                                                                  action
                                                                    .travel_form_details
                                                                    .optionalVisitImage
                                                                );
                                                                setOpenImageDialog(true);
                                                              }}
                                                            >
                                                              <span className="ml-1 text-xs">
                                                                View
                                                              </span>
                                                              <ArrowRight className="h-3 w-3 ml-1" />
                                                            </button>
                                                          ) : (
                                                            <span className="text-sm text-gray-500">
                                                              No image
                                                            </span>
                                                          )}
                                                        </div>
                                                        </div>

        </div>
      </div>
    )}

    {/* BO Visit or RO Visit Section */}
    {(action.specific_action === "BO Visit" || action.specific_action === "RO Visit") && (
      <div className="mb-4 animate-fadeIn">
        <div className="flex items-center text-sm font-medium text-gray-800 mb-2">
          <MapPin size={16} className="mr-2 text-blue-500" />
          <span>Visit Information</span>
        </div>
        <div className="bg-gray-50 p-3 rounded-lg shadow-sm hover:shadow-md transition-shadow">
          {action.specific_action === "RO Visit" && (
            <div className="flex justify-between items-center py-2 border-b border-gray-200">
              <span className="text-sm font-medium">Regional Office:</span>
              <span className="text-sm capitalize">{action?.travel_form_details?.regionalOfficeName || "-"}</span>
            </div>
          )}
          
          {action.specific_action === "BO Visit" && (
            <div className="flex justify-between items-center py-2 border-b border-gray-200">
              <span className="text-sm font-medium">Branch Office:</span>
              <span className="text-sm capitalize">{action?.travel_form_details?.branchName || "-"}</span>
            </div>
          )}
          
          <div className="flex justify-between items-center py-2 border-b border-gray-200">
            <span className="text-sm font-medium">Purpose:</span>
            <span className="text-sm">{action?.travel_form_details?.purposeForVisit || "-"}</span>
          </div>
          
          {action?.travel_form_details?.purposeForVisit === "Meeting_with_RH_BM_ZH" && (
            <div className="flex justify-between items-center py-2 border-b border-gray-200">
              <span className="text-sm font-medium">Concern Person:</span>
              <span className="text-sm capitalize">{action?.travel_form_details?.concernPersonName || "-"}</span>
            </div>
          )}
          
          {action?.travel_form_details?.purposeForVisit === "admin_work" && (
            <div className="flex justify-between items-center py-2 border-b border-gray-200">
              <span className="text-sm font-medium">Admin Task:</span>
              <span className="text-sm capitalize">{action?.travel_form_details?.adminTaskSelect || "-"}</span>
            </div>
          )}
          
          <div className="flex justify-between items-center py-2 border-b border-gray-200">
            <span className="text-sm font-medium">Remarks:</span>
            <span className="text-sm break-words">{action?.travel_form_details?.remarks || "-"}</span>
          </div>
          
          <div className="mt-3 space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium">Visit Image:</span>
              {action?.travel_form_details?.mandatoryVisitImage ? (
                <button
                  className="flex items-center justify-center bg-gray-100 hover:bg-gray-200 text-blue-500 px-3 py-1 rounded transition-colors"
                  onClick={() => {
                    setSelectedImage(action.travel_form_details.mandatoryVisitImage);
                    setOpenImageDialog(true);
                  }}
                >
                  
                  <span className="ml-1 text-xs">View</span><ArrowRight className="h-3 w-3 ml-1" />
                </button>
              ) : (
                <span className="text-sm text-gray-500">No image</span>
              )}
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium">Optional Visit Image:</span>
              {action?.travel_form_details?.optionalVisitImage ? (
                <button
                  className="flex items-center justify-center bg-gray-100 hover:bg-gray-200 text-blue-500 px-3 py-1 rounded transition-colors"
                  onClick={() => {
                    setSelectedImage(action.travel_form_details.optionalVisitImage);
                    setOpenImageDialog(true);
                  }}
                >
                  
                  <span className="ml-1 text-xs">View</span><ArrowRight className="h-3 w-3 ml-1" />
                </button>
              ) : (
                <span className="text-sm text-gray-500">No image</span>
              )}
            </div>
          </div>
        </div>
      </div>
    )}

    {/* HO Visit Section */}
    {action.specific_action === "HO Visit" && (
      <div className="mb-4 animate-fadeIn">
        <div className="flex items-center text-sm font-medium text-gray-800 mb-2">
          <MapPin size={16} className="mr-2 text-blue-500" />
          <span>Head Office Visit</span>
        </div>
        <div className="bg-gray-50 p-3 rounded-lg shadow-sm hover:shadow-md transition-shadow">
          <div className="flex justify-between items-center py-2 border-b border-gray-200">
            <span className="text-sm font-medium">Purpose:</span>
            <span className="text-sm capitalize">{action?.travel_form_details?.hoSelection || "-"}</span>
          </div>
          
          <div className="flex justify-between items-center py-2 border-b border-gray-200">
            <span className="text-sm font-medium">Remarks:</span>
            <span className="text-sm break-words">{action?.travel_form_details?.remarks || "-"}</span>
          </div>
          
          <div className="mt-3 space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium">Visit Image:</span>
              {action?.travel_form_details?.mandatoryVisitImage ? (
                <button
                  className="flex items-center justify-center bg-gray-100 hover:bg-gray-200 text-blue-500 px-3 py-1 rounded transition-colors"
                  onClick={() => {
                    setSelectedImage(action.travel_form_details.mandatoryVisitImage);
                    setOpenImageDialog(true);
                  }}
                >
                  
                  <span className="ml-1 text-xs">View</span> <ArrowRight className="h-3 w-3 ml-1" />
                </button>
              ) : (
                <span className="text-sm text-gray-500">No image</span>
              )}
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium">Optional Visit Image:</span>
              {action?.travel_form_details?.optionalVisitImage ? (
                <button
                  className="flex items-center justify-center bg-gray-100 hover:bg-gray-200 text-blue-500 px-3 py-1 rounded transition-colors"
                  onClick={() => {
                    setSelectedImage(action.travel_form_details.optionalVisitImage);
                    setOpenImageDialog(true);
                  }}
                >
                  
                  <span className="ml-1 text-xs">View</span> <ArrowRight className="h-3 w-3 ml-1" />
                </button>
              ) : (
                <span className="text-sm text-gray-500">No image</span>
              )}
            </div>
          </div>
        </div>
      </div>
    )}

    {/* Meeting Office or Meeting Section */}
    {(action.specific_action === "Meeting_Office" || action.specific_action === "Meeting") && (
      <div className="mb-4 animate-fadeIn">
        <div className="flex items-center text-sm font-medium text-gray-800 mb-2">
          <Users size={16} className="mr-2 text-green-500" />
          <span>Meeting Information</span>
        </div>
        <div className="bg-gray-50 p-3 rounded-lg shadow-sm hover:shadow-md transition-shadow">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
            <div className="flex flex-col py-2">
              <span className="text-xs text-gray-500">Customer Name</span>
              <span className="text-sm font-medium">{action?.Lead?.CustomerName || "-"}</span>
            </div>
            
            <div className="flex flex-col py-2">
              <span className="text-xs text-gray-500">Location</span>
              <span className="text-sm font-medium">{action?.Lead?.location || "-"}</span>
            </div>
            
            {action?.regionalOffice && (
              <div className="flex flex-col py-2">
                <span className="text-xs text-gray-500">Regional Office</span>
                <span className="text-sm font-medium">{action.regionalOffice}</span>
              </div>
            )}
            
            {action?.branchOffice && (
              <div className="flex flex-col py-2">
                <span className="text-xs text-gray-500">Branch Office</span>
                <span className="text-sm font-medium">{action.branchOffice}</span>
              </div>
            )}
            
            <div className="flex flex-col py-2">
              <span className="text-xs text-gray-500">Mobile Number</span>
              <span className="text-sm font-medium">{action?.Lead?.MobileNo || "-"}</span>
            </div>
            
            <div className="flex flex-col py-2">
              <span className="text-xs text-gray-500">Category</span>
              <span className="text-sm font-medium capitalize">{action?.Lead?.category || "-"}</span>
            </div>
            
            <div className="flex flex-col py-2">
              <span className="text-xs text-gray-500">Sub Category</span>
              <span className="text-sm font-medium">{action?.Lead?.sub_category || "-"}</span>
            </div>
          </div>
          
          <div className="mt-3 pt-3 border-t border-gray-200">
            <span className="text-xs text-gray-500">Remarks</span>
            <p className="text-sm mt-1">{action?.Lead?.bdm_remark || "-"}</p>
          </div>
        </div>
      </div>
    )}

    {/* Meeting Field, On Call Discussion, or Site Visit Section */}
    {(action.specific_action === "Meeting_Field" || action.specific_action === "On Call Discussion" || action.specific_action === "Site Visit") && (
      <div className="mb-4 animate-fadeIn">
        <div className="flex items-center text-sm font-medium text-gray-800 mb-2">
          {action.specific_action === "On Call Discussion" ? (
            <Phone size={16} className="mr-2 text-blue-500" />
          ) : action.specific_action === "Site Visit" ? (
            <Map size={16} className="mr-2 text-green-500" />
          ) : (
            <Users size={16} className="mr-2 text-green-500" />
          )}
          <span>{action.specific_action.replace('_', ' ')}</span>
        </div>
        <div className="bg-gray-50 p-3 rounded-lg shadow-sm hover:shadow-md transition-shadow">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
            <div className="flex flex-col py-2">
              <span className="text-xs text-gray-500">Customer Name</span>
              <span className="text-sm font-medium">{action?.Lead?.CustomerName || "-"}</span>
            </div>
            
            <div className="flex flex-col py-2">
              <span className="text-xs text-gray-500">Location</span>
              <span className="text-sm font-medium">{action?.Lead?.location || "-"}</span>
            </div>
            
            <div className="flex flex-col py-2">
              <span className="text-xs text-gray-500">Mobile Number</span>
              <span className="text-sm font-medium">{action?.Lead?.MobileNo || "-"}</span>
            </div>
            
            <div className="flex flex-col py-2">
              <span className="text-xs text-gray-500">Category</span>
              <span className="text-sm font-medium capitalize">{action?.Lead?.category || "-"}</span>
            </div>
            
            <div className="flex flex-col py-2">
              <span className="text-xs text-gray-500">Sub Category</span>
              <span className="text-sm font-medium">{action?.Lead?.sub_category || "-"}</span>
            </div>
          </div>
          
          <div className="mt-3 pt-3 border-t border-gray-200">
            <span className="text-xs text-gray-500">Remarks</span>
            <p className="text-sm mt-1">{action?.Lead?.bdm_remark || "-"}</p>
          </div>
        </div>
      </div>
    )}

    {/* Remarks Section for Agreement, Complaint/Service, Other, Expo Event, Group Meeting */}
    {(action?.specific_action === "Agreement" || 
      action?.specific_action === "Complaint/ Service" || 
      action?.specific_action === "Other" || 
      action?.specific_action === "Expo Event" || 
      action?.specific_action === "Group Meeting") && (
      <div className="mb-4 animate-fadeIn">
        <div className="flex items-center text-sm font-medium text-gray-800 mb-2">
          <MessageSquare size={16} className="mr-2 text-purple-500" />
          <span>Remarks</span>
        </div>
        <div className="bg-gray-50 p-3 rounded-lg shadow-sm hover:shadow-md transition-shadow">
          <p className="text-sm">{action.remarks}</p>
        </div>
      </div>
    )}

    {/* Follow-up Date Section */}
    {action?.new_follow_up_date && (
      <div className="mb-4 animate-fadeIn">
        <div className="flex items-center text-sm font-medium text-gray-800 mb-2">
          <Calendar size={16} className="mr-2 text-red-500" />
          <span>Follow-up Date</span>
        </div>
        <div className="bg-gray-50 p-3 rounded-lg shadow-sm hover:shadow-md transition-shadow">
          <p className="text-sm font-medium">{formatDate(action.new_follow_up_date)}</p>
        </div>
      </div>
    )}
  </div>
)}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-gray-500 italic">
                              No actions recorded
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500 text-sm">
                  No region data available
                </p>
              </div>
            )}
          </div>
        </div>
        {/* Bottom info */}
        <div className="text-xs text-gray-500 text-center mt-6 px-4">
          Last updated: {new Date().toLocaleString()}
        </div>

        <Dialog
          open={openImageDialog}
          onClose={() => setOpenImageDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogContent
            sx={{ p: 0, position: "relative", backgroundColor: "black" }}
          >
            {/* Close Button */}
            <IconButton
              aria-label="close"
              onClick={() => {
                setOpenImageDialog(false);
                setSelectedImage(null);
              }}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: "#fff",
                zIndex: 1,
              }}
            >
              <CloseIcon />
            </IconButton>

            {/* Image */}
            <img
              src={selectedImage}
              alt="Visit"
              style={{ width: "100%", height: "auto", display: "block" }}
            />
          </DialogContent>
        </Dialog>
      </>
    </>
  );
};

export default MobileView;
